import React, { useMemo } from "react";
import styled from "styled-components";
import NVLineChartWithLegends from "common/graphs/NVLineChart";
import * as d3 from "d3";
import Loading from "common/Loading";
import { parseVolumeData } from "views/Statistics/Congestion/CongestionAndACM/api";
import {
  PERIOD_DASHBOARD_TO_HOURS,
  PERIOD_TO_VOLUME_INTERVAL,
  LOAD_INTERVAL,
} from "./utils";
import useDashboardData from "./useDashboardData";
import { atBqnTarget } from "common/api";

const Wrapper = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;

  svg {
    height: 200px;

    .nv-axisMaxMin.nv-axisMaxMin-y.nv-axisMax-y {
      display: none;
    }
  }
`;

const WrapperLoader = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 150px;
  color: var(--text-primary);
`;

const getThroughPutData = ({ mode, bqnId, period }) => {
  const hours = PERIOD_DASHBOARD_TO_HOURS[period];
  const interval = PERIOD_TO_VOLUME_INTERVAL[period];

  return ifCl
    .run(
      atBqnTarget(
        `show statistics volume mbps-max hours ${hours} interval ${interval}`,
        mode,
        bqnId,
      ),
    )
    .then(parseVolumeData);
};

const fieldLabelMap = {
  Dn: "Mbps-downlink",
  Up: "Mbps-uplink",
}

function Throughput({ period }) {
  // The chart has a cadence of its own: it draws no range of the dashboard,
  // so nothing else asks for its data again.
  const { data, isLoading } = useDashboardData({
    retrieve: ({ mode, bqnId }) => getThroughPutData({ mode, bqnId, period }),
    onError: "Error fetching throughput data:",
    deps: [period],
    interval: LOAD_INTERVAL,
  });

  // The fields are what the chart compares to decide whether it has to be
  // drawn again, so they are given the same identity while they are the same.
  const { items, fields } = data || {};
  const displayFields = useMemo(
    () =>
      (fields ?? []).map((field) => ({
        name: field,
        label: fieldLabelMap[field] || field,
      })),
    [fields],
  );

  if (isLoading) {
    return (
      <WrapperLoader>
        <Loading />
      </WrapperLoader>
    );
  }

  if (!data) {
    return (
      <WrapperLoader>
        No data available
      </WrapperLoader>
    );
  }

  return (
    <Wrapper>
      <NVLineChartWithLegends
        className="graph"
        items={items}
        yAxisUnits={"Mbps"}
        xAxisFormat={d3.timeFormat("%m/%d %H:%M")}
        xField="time"
        yAxisTopGap={0.1}
        yAxisTicksNumber={5}
        fields={displayFields}
        fixedYRange={[0, 1]}
        yAxisFormat={d3.format(".1f")}
      />
    </Wrapper>
  );
}

export default React.memo(Throughput);
